/*
 * Enova - Carrusel móvil para secciones multicolumna
 * ==================================================
 * El slider móvil del tema es scroll libre y deja ~50px de la siguiente
 * tarjeta a la vista. Este elemento lo convierte en un carrusel de una
 * tarjeta completa por vista, con flechas laterales y rotación automática.
 * Solo actúa por debajo del breakpoint móvil (750px); en escritorio la
 * grilla se muestra completa y el elemento no hace nada.
 */

(function () {
  const selectors = {
    slider: '.grid--mobile-carousel',
    slide: '.grid-item',
    buttonPrev: '[data-carousel-prev]',
    buttonNext: '[data-carousel-next]',
  };

  const classes = {
    scrolling: 'is-scrolling',
  };

  const settings = {
    mobileBreakpoint: 750,
    scrollSettleDelay: 100,
    defaultSpeed: 3,
  };

  if (customElements.get('mobile-carousel')) return;

  customElements.define(
    'mobile-carousel',

    class MobileCarousel extends HTMLElement {
      constructor() {
        super();

        this.index = 0;
        this.autoplayTimer = null;
        this.scrollTimer = null;
        this.inView = true;

        this.onPrevClick = () => this.goTo(this.index - 1, true);
        this.onNextClick = () => this.goTo(this.index + 1, true);
        this.onScroll = () => this.handleScroll();
        this.onPointerDown = () => this.stopAutoplay();
        this.onVisibilityChange = () => this.syncAutoplay();
        this.onResize = () => this.syncAutoplay();
      }

      connectedCallback() {
        this.slider = this.querySelector(selectors.slider);
        if (!this.slider) return;

        this.slides = Array.from(this.slider.querySelectorAll(selectors.slide));
        if (this.slides.length < 2) return;

        this.buttonPrev = this.querySelector(selectors.buttonPrev);
        this.buttonNext = this.querySelector(selectors.buttonNext);

        const speed = parseFloat(this.getAttribute('data-autoplay-speed'));
        this.speed = (speed > 0 ? speed : settings.defaultSpeed) * 1000;
        this.autoplayEnabled =
          this.hasAttribute('autoplay') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (this.buttonPrev) this.buttonPrev.addEventListener('click', this.onPrevClick);
        if (this.buttonNext) this.buttonNext.addEventListener('click', this.onNextClick);

        this.slider.addEventListener('scroll', this.onScroll, {passive: true});
        this.slider.addEventListener('pointerdown', this.onPointerDown, {passive: true});
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('theme:resize:width', this.onResize);

        // Pausa la rotación mientras la sección está fuera de pantalla.
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.syncAutoplay();
        });
        this.observer.observe(this);

        this.syncAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        clearTimeout(this.scrollTimer);

        if (this.buttonPrev) this.buttonPrev.removeEventListener('click', this.onPrevClick);
        if (this.buttonNext) this.buttonNext.removeEventListener('click', this.onNextClick);

        if (this.slider) {
          this.slider.removeEventListener('scroll', this.onScroll);
          this.slider.removeEventListener('pointerdown', this.onPointerDown);
        }

        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.removeEventListener('theme:resize:width', this.onResize);

        if (this.observer) this.observer.disconnect();
      }

      isMobile() {
        const width = window.theme && window.theme.getWindowWidth ? window.theme.getWindowWidth() : window.innerWidth;

        return width < settings.mobileBreakpoint;
      }

      goTo(index, fromUser) {
        const total = this.slides.length;
        const target = ((index % total) + total) % total;
        this.index = target;

        // El snap obligatorio pelea con el scroll programático en Safari:
        // se desactiva mientras dura la animación y vuelve al asentarse.
        this.slider.classList.add(classes.scrolling);
        this.slider.scrollTo({
          left: this.slides[target].offsetLeft - this.slides[0].offsetLeft,
          behavior: 'smooth',
        });

        if (fromUser) this.restartAutoplay();
      }

      handleScroll() {
        clearTimeout(this.scrollTimer);

        this.scrollTimer = setTimeout(() => {
          const base = this.slides[0].offsetLeft;
          const position = this.slider.scrollLeft;
          let closest = 0;
          let shortest = Infinity;

          this.slides.forEach((slide, index) => {
            const distance = Math.abs(slide.offsetLeft - base - position);

            if (distance < shortest) {
              shortest = distance;
              closest = index;
            }
          });

          this.index = closest;
          this.slider.classList.remove(classes.scrolling);
          this.syncAutoplay();
        }, settings.scrollSettleDelay);
      }

      syncAutoplay() {
        const shouldRun = this.autoplayEnabled && this.inView && this.isMobile() && !document.hidden;

        if (shouldRun) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      }

      startAutoplay() {
        if (this.autoplayTimer) return;

        this.autoplayTimer = setInterval(() => this.goTo(this.index + 1), this.speed);
      }

      stopAutoplay() {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      restartAutoplay() {
        this.stopAutoplay();
        this.syncAutoplay();
      }
    }
  );
})();
